import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Inbox, Loader2, UserPlus } from "lucide-react";

export const InboundUnmatched = ({ cases }) => {
  const [items, setItems] = useState([]);
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/admin/inbound", { params: { status: "unmatched" } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const assign = async (id) => {
    if (!pick[id]) { toast.error("Pick a client first"); return; }
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/inbound/${id}/assign`, { user_id: pick[id] });
      toast.success(data.status === "processed" ? `Filed ${data.receipt_ids?.length || 0} receipt(s) to the client's vault` : "Assigned — no receipt found in that email");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not assign"); } finally { setBusy(null); }
  };

  if (!items.length) return null;
  const clients = (cases || []).filter((c) => c.user_email).map((c) => ({ user_id: c.user_id, label: `${c.user_name || c.user_email} · ${c.business_name}` }));

  return (
    <div className="bg-white border border-[#FDE68A] rounded-2xl p-6" data-testid="inbound-unmatched">
      <div className="flex items-center gap-2 text-[#92600A]"><Inbox className="h-5 w-5" /><span className="font-heading font-semibold text-[#1C1C1C]">Emailed receipts we couldn't match</span>
        <span className="text-xs font-bold bg-[#FEF3C7] text-[#92600A] px-2 py-0.5 rounded-full" data-testid="inbound-unmatched-count">{items.length}</span></div>
      <p className="mt-1 text-sm text-[#595D5A]">Sent from an address we don't recognize. Pick the client and we'll file it to their vault.</p>
      <ul className="mt-4 space-y-2">
        {items.map((m) => (
          <li key={m.email_id} className="flex flex-col sm:flex-row sm:items-center gap-2 bg-[#F9F9F8] rounded-xl px-3 py-2.5" data-testid={`inbound-item-${m.email_id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#1C1C1C] truncate">{m.subject || "(no subject)"}</p>
              <p className="text-xs text-[#595D5A] truncate">from {m.from} · {m.attachment_count} attachment(s) · {new Date(m.received_at).toLocaleDateString()}</p>
            </div>
            <Select value={pick[m.email_id] || ""} onValueChange={(v) => setPick({ ...pick, [m.email_id]: v })}>
              <SelectTrigger className="rounded-xl sm:w-64" data-testid={`inbound-pick-${m.email_id}`}><SelectValue placeholder="Assign to client…" /></SelectTrigger>
              <SelectContent className="max-h-64">{clients.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={() => assign(m.email_id)} disabled={busy === m.email_id} size="sm" data-testid={`inbound-assign-${m.email_id}`} className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white">
              {busy === m.email_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-1" /> File</>}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default InboundUnmatched;
